import React, { useEffect, useMemo, useState, Component } from 'react';
import { Shield, FileText, Database, Eye, Download, Filter, Award } from 'lucide-react';
import { getAllQuestions, QUESTION_DATA_VERSION } from '../../../data/questionLoader';
import { MODULES } from '../../../data/modules';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { GovernanceReport } from './GovernanceReport';
import { ContentQuality } from './ContentQuality';
import { SeedAudit } from './SeedAudit';
import { ValidationSuite } from './ValidationSuite';
import { QuestionQualityPanel } from './QuestionQualityPanel';
import { DidacticDepthPanel } from './DidacticDepthPanel';
import { buildQualityQuestionDataset } from './qualityQuestionDataset';
import { buildQualityReport } from '../../../governance/questionQualityEngine';
import { QualityTab, QualityWorkspaceProvider, useQualityWorkspaceState } from './useQualityWorkspaceState';
import { exportQualityData, QualityExportFormat } from './qualityExport';
const TABS: Array<{
  id: QualityTab;
  label: string;
  shortLabel: string;
  icon: ComponentType<{
    className?: string;
  }>;
  color: string;
}> = [{
  id: 'governance',
  label: 'Governance',
  shortLabel: 'Gov',
  icon: Shield,
  color: 'text-purple-600'
}, {
  id: 'quality',
  label: 'Question Quality',
  shortLabel: 'Quality',
  icon: Award,
  color: 'text-amber-500'
}, {
  id: 'didactic-depth',
  label: 'Didactic Depth',
  shortLabel: 'Depth',
  icon: Award,
  color: 'text-indigo-600'
}, {
  id: 'content',
  label: 'Content',
  shortLabel: 'Content',
  icon: FileText,
  color: 'text-blue-600'
}, {
  id: 'seeds',
  label: 'Seeds',
  shortLabel: 'Seeds',
  icon: Database,
  color: 'text-emerald-600'
}, {
  id: 'visuals',
  label: 'Visuals',
  shortLabel: 'Visuals',
  icon: Eye,
  color: 'text-indigo-600'
}];
export function QualityWorkspace({
  refreshKey = 0,
  initialTab
}: {
  refreshKey?: number;
  initialTab?: QualityTab;
}) {
  const records = useMemo(() => {
    const questions = getAllQuestions();
    return buildQualityQuestionDataset(questions);
  }, [refreshKey, QUESTION_DATA_VERSION]);
  // NEW-BUG-2 fix: compute qualityReport once here, pass down to avoid
  // QuestionQualityPanel recomputing it independently (was a 3rd validation pass)
  const qualityReport = useMemo(() => {
    const questions = records.map(r => r.question);
    return buildQualityReport(questions);
  }, [records]);
  return <QualityWorkspaceProvider records={records} data-id="element-2501">
      <WorkspaceInner refreshKey={refreshKey} qualityReport={qualityReport} initialTab={initialTab} data-id="element-2502" />
    </QualityWorkspaceProvider>;
}
function WorkspaceInner({
  refreshKey = 0,
  qualityReport,
  initialTab
}: {
  refreshKey?: number;
  qualityReport: ReturnType<typeof buildQualityReport>;
  initialTab?: QualityTab;
}) {
  const {
    records,
    filteredRecords,
    activeTab,
    setActiveTab,
    selectedModule,
    setSelectedModule,
    selectedTier,
    setSelectedTier,
    selectedGroup,
    setSelectedGroup,
    search,
    setSearch,
    severity,
    setSeverity
  } = useQualityWorkspaceState();
  const [format, setFormat] = useState<QualityExportFormat>('csv');
  const [showFilters, setShowFilters] = useState(false);
  // Handle initial tab prop
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab, setActiveTab]);
  // Keep event listener as fallback for now, but prefer prop-based navigation
  useEffect(() => {
    const onSetTab = (event: Event) => {
      const customEvent = event as CustomEvent<QualityTab>;
      if (customEvent.detail) setActiveTab(customEvent.detail);
    };
    window.addEventListener('quality-workspace:set-tab', onSetTab);
    return () => window.removeEventListener('quality-workspace:set-tab', onSetTab);
  }, [setActiveTab]);
  const totals = useMemo(() => {
    const allIssues = records.flatMap(r => r.issues);
    const qualityScores = records.map(r => r.qualityScore).filter((s): s is number => s !== undefined);
    const avgQuality = qualityScores.length > 0 ? Math.round(qualityScores.reduce((a, b) => a + b, 0) / qualityScores.length) : 0;
    const genericFeedbackCount = records.filter(r => r.qualityFlags?.some(f => f.label === 'Generic feedback')).length;
    const poorQualityCount = records.filter(r => (r.qualityScore ?? 100) < 40).length;
    return {
      questions: records.length,
      issues: allIssues.length,
      critical: allIssues.filter(i => i.severity === 'critical').length,
      warnings: allIssues.filter(i => i.severity === 'warning').length,
      info: allIssues.filter(i => i.severity === 'info').length,
      avgQuality,
      genericFeedbackCount,
      poorQualityCount
    };
  }, [records]);
  const filteredTotals = useMemo(() => {
    const tabIssues = filteredRecords.flatMap(r => r.issues.filter(i => i.category === activeTab));
    return {
      questions: filteredRecords.length,
      issues: tabIssues.length,
      critical: tabIssues.filter(i => i.severity === 'critical').length,
      warnings: tabIssues.filter(i => i.severity === 'warning').length
    };
  }, [filteredRecords, activeTab]);
  const exportRows = filteredRecords.flatMap(record => record.issues.filter(issue => issue.category === activeTab).map(issue => ({
    questionId: record.question.id,
    module: record.question.module,
    tier: record.question.tier,
    group: record.question.group,
    category: issue.category,
    severity: issue.severity,
    message: issue.message
  })));
  const tiers = Array.from(new Set(records.map(r => r.question.tier))).sort((a, b) => a - b);
  const activeFiltersCount = [selectedModule !== 'all', selectedTier !== 'all', selectedGroup !== 'all', search.trim() !== '', severity !== 'all'].filter(Boolean).length;
  return <div className="space-y-6" data-id="element-2503">
      {/* Header KPIs */}
      <Card className="p-4 sm:p-5" data-id="element-2504">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4" data-id="element-2505">
          <div className="flex-1 min-w-0" data-id="element-2506">
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 truncate" data-id="element-2507">
              Quality Workspace
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5" data-id="element-2508">
              {records.length} questions analyzed across all modules
            </p>
          </div>
          <Badge variant={totals.critical > 0 ? 'danger' : totals.warnings > 0 ? 'warning' : 'success'} size="lg" className="self-start sm:self-auto" data-id="element-2509">
            {totals.critical > 0 ? `${totals.critical} Critical` : totals.warnings > 0 ? `${totals.warnings} Warnings` : 'All Clear'}
          </Badge>
        </div>

        {/* Row 1: Issue counts */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 mb-3" data-id="element-2510">
          <KpiCard label="Questions" value={totals.questions} color="slate" data-id="element-2511" />
          <KpiCard label="Total Issues" value={totals.issues} color="slate" data-id="element-2512" />
          <KpiCard label="Critical" value={totals.critical} color={totals.critical > 0 ? 'rose' : 'emerald'} data-id="element-2513" />
          <KpiCard label="Warnings" value={totals.warnings} color={totals.warnings > 0 ? 'amber' : 'emerald'} data-id="element-2514" />
          <KpiCard label="Info" value={totals.info} color="blue" data-id="element-2515" />
        </div>

        {/* Row 2: Quality-specific KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t border-slate-100" data-id="element-2516">
          <QualityKpiCard label="Avg Quality Score" value={totals.avgQuality} suffix="/ 100" color={totals.avgQuality >= 70 ? 'emerald' : totals.avgQuality >= 50 ? 'amber' : 'rose'} showBar data-id="element-2517" />
          <QualityKpiCard label="Generic Feedback" value={totals.genericFeedbackCount} suffix="questions" color={totals.genericFeedbackCount > 0 ? 'rose' : 'emerald'} description="Boilerplate explanations" data-id="element-2518" />
          <QualityKpiCard label="Poor Quality" value={totals.poorQualityCount} suffix="questions" color={totals.poorQualityCount > 0 ? 'rose' : 'emerald'} description="Score < 40 / 100" data-id="element-2519" />
        </div>
      </Card>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1 bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto scrollbar-hide" data-id="element-2520">
        {TABS.map(tab => {
        const Icon = tab.icon;
        const tabIssues = records.flatMap(r => r.issues.filter(i => i.category === tab.id));
        const tabCritical = tabIssues.filter(i => i.severity === 'critical').length;
        const isActive = activeTab === tab.id;
        return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex-shrink-0 flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 whitespace-nowrap ${isActive ? 'bg-slate-900 text-white shadow-md' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-2521">
              <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-white' : tab.color}`} data-id="element-2522" />
              <span className="hidden sm:inline" data-id="element-2523">{tab.label}</span>
              <span className="inline sm:hidden" data-id="element-2524">{tab.shortLabel}</span>
              {tabCritical > 0 && <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full shrink-0 ${isActive ? 'bg-rose-500 text-white' : 'bg-rose-100 text-rose-700'}`} data-id="element-2525">
                  {tabCritical}
                </span>}
            </button>;
      })}
      </div>

      {/* Filter Bar */}
      <Card className="p-4" data-id="element-2526">
        <div className="flex flex-col gap-3" data-id="element-2527">
          {/* Top row: Filter toggle and stats */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-2528">
            <button onClick={() => setShowFilters(v => !v)} className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors self-start" data-id="element-2529">
              <Filter className="w-4 h-4" data-id="element-2530" />
              Filters
              {activeFiltersCount > 0 && <Badge variant="primary" size="sm" data-id="element-2531">
                  {activeFiltersCount} active
                </Badge>}
            </button>

            {/* Export controls */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3" data-id="element-2532">
              <span className="text-xs text-slate-500 whitespace-nowrap" data-id="element-2533">
                {filteredTotals.questions} questions · {filteredTotals.issues}{' '}
                issues
              </span>
              <div className="flex gap-2" data-id="element-2534">
                <select value={format} onChange={e => setFormat(e.target.value as QualityExportFormat)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-300" data-id="element-2535">
                  <option value="csv" data-id="element-2536">CSV</option>
                  <option value="json" data-id="element-2537">JSON</option>
                  <option value="markdown" data-id="element-2538">Markdown</option>
                </select>
                <Button size="sm" variant="secondary" onClick={() => exportQualityData(`quality-${activeTab}`, format, exportRows)} className="gap-1.5" data-id="element-2539">
                  <Download className="w-3.5 h-3.5" data-id="element-2540" />
                  <span className="hidden sm:inline" data-id="element-2541">Export</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Filter controls */}
          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2 pt-3 border-t border-slate-100" data-id="element-2542">
              <select value={selectedModule} onChange={e => setSelectedModule(e.target.value as any)} className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-300 w-full" data-id="element-2543">
                <option value="all" data-id="element-2544">All modules</option>
                {MODULES.map(m => <option key={m.id} value={m.id} data-id="element-2545">
                    {m.id}
                  </option>)}
              </select>
              <select value={selectedTier} onChange={e => setSelectedTier(e.target.value === 'all' ? 'all' : Number(e.target.value))} className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-300 w-full" data-id="element-2546">
                <option value="all" data-id="element-2547">All tiers</option>
                {tiers.map(tier => <option key={tier} value={tier} data-id="element-2548">
                    Tier {tier}
                  </option>)}
              </select>
              <select value={selectedGroup} onChange={e => setSelectedGroup(e.target.value === 'all' ? 'all' : Number(e.target.value))} className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-300 w-full" data-id="element-2549">
                <option value="all" data-id="element-2550">All groups</option>
                {[6, 7, 8].map(g => <option key={g} value={g} data-id="element-2551">
                    Group {g}
                  </option>)}
              </select>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search ID / family / text…" className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 w-full sm:col-span-2 lg:col-span-1" data-id="element-2552" />
              <select value={severity} onChange={e => setSeverity(e.target.value as any)} className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-300 w-full" data-id="element-2553">
                <option value="all" data-id="element-2554">All severity</option>
                <option value="critical" data-id="element-2555">Critical</option>
                <option value="warning" data-id="element-2556">Warning</option>
                <option value="info" data-id="element-2557">Info</option>
              </select>
            </div>}
        </div>
      </Card>

      {/* Tab Content */}
      {activeTab === 'governance' && <GovernanceReport data-id="element-2558" />}
      {activeTab === 'quality' && <QuestionQualityPanel report={qualityReport} data-id="element-2559" />}
      {activeTab === 'didactic-depth' && <DidacticDepthPanel data-id="element-2560" />}
      {activeTab === 'content' && <ContentQuality data-id="element-2561" />}
      {activeTab === 'seeds' && <SeedAudit refreshKey={refreshKey} data-id="element-2562" />}
      {activeTab === 'visuals' && <ValidationSuite data-id="element-2563" />}
    </div>;
}
function KpiCard({
  label,
  value,
  color
}: {
  label: string;
  value: number;
  color: 'slate' | 'rose' | 'amber' | 'emerald' | 'blue';
}) {
  const colors = {
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700'
  };
  return <div className={`rounded-xl border p-3 text-center ${colors[color]}`} data-id="element-2564">
      <div className="text-xl sm:text-2xl font-black" data-id="element-2565">{value}</div>
      <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider opacity-70 mt-0.5" data-id="element-2566">
        {label}
      </div>
    </div>;
}
function QualityKpiCard({
  label,
  value,
  suffix,
  color,
  showBar,
  description
}: {
  label: string;
  value: number;
  suffix: string;
  color: 'rose' | 'amber' | 'emerald';
  showBar?: boolean;
  description?: string;
}) {
  const colors = {
    rose: {
      bg: 'bg-rose-50',
      border: 'border-rose-200',
      text: 'text-rose-700',
      bar: 'bg-rose-400'
    },
    amber: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      bar: 'bg-amber-400'
    },
    emerald: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-700',
      bar: 'bg-emerald-400'
    }
  };
  const c = colors[color];
  return <div className={`rounded-xl border p-3 sm:p-4 ${c.bg} ${c.border}`} data-id="element-2567">
      <div className="flex items-baseline gap-1.5 mb-1" data-id="element-2568">
        <span className={`text-xl sm:text-2xl font-black ${c.text}`} data-id="element-2569">
          {value}
        </span>
        <span className={`text-xs font-medium opacity-60 ${c.text}`} data-id="element-2570">
          {suffix}
        </span>
      </div>
      <div className={`text-xs font-bold uppercase tracking-wider ${c.text} opacity-80`} data-id="element-2571">
        {label}
      </div>
      {description && <div className={`text-xs mt-0.5 ${c.text} opacity-60`} data-id="element-2572">
          {description}
        </div>}
      {showBar && <div className="mt-2 h-1.5 w-full bg-white/60 rounded-full overflow-hidden" data-id="element-2573">
          <div className={`h-full rounded-full ${c.bar} transition-all duration-500`} style={{
        width: `${value}%`
      }} data-id="element-2574" />
        </div>}
    </div>;
}